import React from 'react';
import uuidv4 from 'uuid/v4';
import BaseModel from "../base_model.js";
import I18n from "../../base/i18n.js.jsx";

import ProjectStatusEnum from "../../enums/project_status.js";
import ProjectPublishedFilterModeEnum from "../../enums/project_published_filter_mode.js";
import ProjectMediationTypeEnum from '../../enums/project_mediation_type.js.jsx';

import ListCompaniesUseCase from "../../use_cases/admin/companies/companies/list_companies_use_case.js";
import ListDevelopersUseCase from "../../use_cases/admin/projects/developers/list_developers_use_case.js";
import UploadPdfUseCase from "../../use_cases/assets/pdfs/upload_pdf_use_case.js";

import { ProjectHighlightModel } from "./project_highlight_model.js";
import { ProjectImageModel } from "./project_image_model.js";
import { ProjectDocumentModel } from "./project_document_model.js";
import { AddressModel } from "../addresses/address.js";
import { ProjectDeveloperModel } from "./project_developer_model.js";
import { CompanyModel } from "../companies/company_model.js";

import { AddTypeAuto } from '../../base/form/fields/nested_models_array_field.js';

import UploadButton from "../../base/buttons/upload_button.js";

import ProjectProgressCell from "../../base/table/cells/special_cells/project_progress_cell.js";

import { TopBarHeight } from "../../admin/constants.js";
import { ProjectBonusRateModel } from './project_bonus_rate_model.js.jsx';
import ProjectProcessEnum from '../../enums/project_process.js.jsx';

class RawProjectModel extends BaseModel {

  rawListFilters() {
    return [
      {
        name: "statuses",
        displayName: this.fieldDisplayName("status"),
        type: "enum",
        enum: ProjectStatusEnum,
        allowClear: true,
        clearItem: I18n.t("enum.project_status.all"),
        multiple: true
      },
      {
        name: "company",
        displayName: this.fieldDisplayName("company"),
        type: "model",
        useCase: ListCompaniesUseCase,
        foreignKey: "companyId",
        allowClear: true,
        clearItem: I18n.t("model.companies.company.all")
      },
      {
        name: "developer",
        displayName: this.fieldDisplayName("developer"),
        type: "model",
        useCase: ListDevelopersUseCase,
        foreignKey: "developerId",
        allowClear: true,
        clearItem: I18n.t("model.projects.developer.all")
      },
      {
        name: "published",
        displayName: this.fieldDisplayName("isPublished"),
        type: "enum",
        enum: ProjectPublishedFilterModeEnum,
        allowClear: true,
        clearItem: I18n.t("enum.project_published_filter_mode.all")
      },
    ]
  }

  rawListFields() {
    return [
      {
        name: "name",
        displayName: this.fieldDisplayName("name"),
        type: "text",
      },
      {
        name: "status",
        displayName: this.fieldDisplayName("status"),
        type: "enum",
        enum: ProjectStatusEnum
      },
      {
        name: "company",
        displayName: this.fieldDisplayName("company"),
        type: "model",
        model: CompanyModel
      },
      {
        name: "developer",
        displayName: this.fieldDisplayName("developer"),
        type: "model",
        model: ProjectDeveloperModel
      },
      {
        name: "progress",
        displayName: this.fieldDisplayName("progress"),
        renderFunction: (project) => {
          return <ProjectProgressCell project = { project } />
        }
      },
      {
        name: "createdAt",
        displayName: this.fieldDisplayName("createdAt"),
        type: "dateTime",
        dateFormat: "DD.MM.YYYY",
        timeFormat: "HH:mm",
        sortKey: "created_at"
      },
      {
        name: "isPublished",
        displayName: this.fieldDisplayName("isPublished"),
        type: "boolean"
      }
    ]
  }

  rawFormFields(item = undefined) {
    return [
      {
        name: "name",
        displayName: this.fieldDisplayName("name"),
        type: "text",
      },
      {
        name: "subtitle",
        displayName: this.fieldDisplayName("subtitle"),
        type: "text",
      },
      {
        name: "projectImages",
        displayName: this.fieldDisplayName("projectImages"),
        type: "nestedModelsArray",
        model: ProjectImageModel,
        weight: 12,
        itemWeight: 4,
        allowDragDrop: true,
        allowDelete: true,
        addType: AddTypeAuto,
        defaultItem: (items) => {
          return {
            clientId: uuidv4(),
            position: items.length + 1,
            loading: true
          }
        },
        addButtonFunc: (items, defaultItem, onAdd, hasError) => {
          const onChange = (files) => {
            let newItems = Object.keys(files).map((index) => {
              let file = files[index];
              return {
                ...defaultItem(items),
                galleryImage: {
                  file: file
                }
              }
            })
            onAdd(newItems)
          }
          return <UploadButton multiple = { true }
                               text = { I18n.t("action.upload_images") }
                               onChange = { onChange }
                               hasError = { hasError }/>
        }
      },
      {
        name: "highlights",
        displayName: this.fieldDisplayName("highlights"),
        type: "nestedModelsArray",
        model: ProjectHighlightModel,
        weight: 12,
        itemWeight: 12,
        allowDragDrop: false,
        allowDelete: true,
        defaultItem: (items) => {
          return {
            clientId: uuidv4(),
            position: items.length
          }
        }
      },
      {
        name: "bonusRates",
        displayName: this.fieldDisplayName("bonusRates"),
        type: "nestedModelsArray",
        model: ProjectBonusRateModel,
        weight: 12,
        itemWeight: 12,
        allowDragDrop: false,
        allowDelete: true,
        defaultItem: (items) => {
          return {
            clientId: uuidv4(),
            position: items.length
          }
        }
      },
      {
        name: "description",
        displayName: this.fieldDisplayName("description"),
        type: "WYSIWYG",
        weight: 12,
        viewportTopOffset: parseInt(TopBarHeight)
      },
      {
        name: "listingOrder",
        displayName: this.fieldDisplayName("listingOrder"),
        type: "decimal",
        precision: 5,
        scale: 0,
        weight: 12,
      },
      {
        name: "mediationType",
        displayName: this.fieldDisplayName("mediation_type"),
        type: "enum",
        enum: ProjectMediationTypeEnum
      },
      {
        name: "annualInterestRate",
        displayName: this.fieldDisplayName("annualInterestRate"),
        type: "decimal",
        precision: 5,
        scale: 2,
      },
      {
        name: "process",
        displayName: this.fieldDisplayName("process"),
        type: "enum",
        enum: ProjectProcessEnum,
      },
      {
        name: "beforeDevelopmentInterestRate",
        displayName: this.fieldDisplayName("beforeDevelopmentInterestRate"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "minInvestmentAmount",
        displayName: this.fieldDisplayName("minInvestmentAmount"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "ratingMark",
        displayName: this.fieldDisplayName("ratingMark"),
        type: "text",
      },
      {
        name: "paidToDeveloperAt",
        displayName: this.fieldDisplayName("paidToDeveloperAt"),
        type: "dateTime",
      },
      {
        name: "concludedAt",
        displayName: this.fieldDisplayName("concludedAt"),
        type: "dateTime",
      },
      {
        name: "secupayContractId",
        displayName: this.fieldDisplayName("secupayContractId"),
        type: "text",
      },
      {
        name: "executionTimeFrom",
        displayName: this.fieldDisplayName("executionTimeFrom"),
        type: "number",
      },
      {
        name: "executionTimeTo",
        displayName: this.fieldDisplayName("executionTimeTo"),
        type: "number",
      },
      {
        name: "endDate",
        displayName: this.fieldDisplayName("end_date"),
        type: "dateTime",
        format: "DD.MM.YYYY",
      },
      {
        name: "address",
        type: "nestedModel",
        model: AddressModel,
        weight: 12
      },
      {
        name: "developer",
        displayName: this.fieldDisplayName("developer"),
        type: "model",
        useCase: ListDevelopersUseCase,
        foreignKey: "developerId",
        allowClear: true,
        clearItem: I18n.t("model.projects.developer.all")
      },
      {
        name: "company",
        displayName: this.fieldDisplayName("company"),
        type: "model",
        useCase: ListCompaniesUseCase,
        foreignKey: "companyId",
      },
      {
        name: "cost",
        displayName: this.fieldDisplayName("cost"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "amountOfOwnCapital",
        displayName: this.fieldDisplayName("amountOfOwnCapital"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "amountOfBankCredit",
        displayName: this.fieldDisplayName("amountOfBankCredit"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "overinvestmentLimit",
        displayName: this.fieldDisplayName("overinvestmentLimit"),
        type: "decimal",
        precision: 12,
        scale: 2,
      },
      {
        name: "staticPartOfContractPdf",
        type: "file",
        allowDelete: true,
        foreignKey: "staticPartOfContractPdfId",
        allowSelfLoad: true,
        forceNextLine: true,
        accept: "application/pdf",
        uploader: (file) => {
          return UploadPdfUseCase.execute(file).then(result => {
            return result.data.pdf;
          })
        }
      },
      {
        name: "documents",
        type: "nestedModelsArray",
        model: ProjectDocumentModel,
        weight: 12,
        itemWeight: 6,
        allowDragDrop: false,
        allowDelete: true,
        defaultItem: (items) => {
          return {
            clientId: uuidv4(),
            loading: true,
            showInConfirmationPage: true,
            attachToEmail: true
          }
        },
        addButtonFunc: (items, defaultItem, onAdd) => {
          const onChange = (files) => {
            let newItems = Object.keys(files).map((index) => {
              let file = files[index];
              return {
                ...defaultItem(items),
                name: file.name,
                pdf: {
                  file: file
                }
              }
            })
            onAdd(newItems)
          }
          return <UploadButton key = "upload"
                               multiple = { true }
                               text = { I18n.t("action.upload_files") }
                               onChange = { onChange }
                               accept = { "application/pdf" }/>
        }
      },


    ]
  }

  displayValue(item) {
    if(!item) {
      return "";
    }
    if(!item.slug) {
      return I18n.t(`${this.i18nScope()}.new`);
    }
    if(!item.name) {
      return `${I18n.t(`${this.i18nScope()}.name`, {count: 1} )} ${item.slug}`;
    }
    return item.name;
  }

  i18nScope() {
    return "model.projects.project";
  }

  value(item) {
    if(!item) {
      return undefined;
    }
    return item.slug;
  }

}
const ProjectModel = new RawProjectModel();

export { RawProjectModel, ProjectModel }
